import { Form, Input as AntdInput } from 'antd';
import React, { FC, memo, useCallback } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { showNotification } from 'app/providers/NotificationsProvider';
import { Button } from 'shared/ui/Button';
import { Modal } from 'shared/ui/Modal';
import { useChangeUserPasswordMutation } from 'entities/User';
import { AppLogo } from 'app/appState';
import { ModernInputPassword } from 'shared/ui/ModernInputPassword';

interface NewPasswordModalProps {
  userId: string;
  isOpened: boolean;
  onClose: () => void;
}

export const NewPasswordModal: FC<NewPasswordModalProps> = memo((props) => {
  const { userId, isOpened, onClose } = props;

  const { t } = useAppTranslation(['profile', 'common']);

  const [form] = Form.useForm();

  const [changePassword, { isLoading }] = useChangeUserPasswordMutation();

  const resetPassword = useCallback(
    async ({ password }: { password: string }) => {
      if (userId) {
        try {
          await changePassword({ userId, params: { password } }).unwrap();
          showNotification('info', t('Success', { ns: 'common' }), t('Password reset has been successful', { ns: 'common' }));
          onClose();
        } catch (error: CustomAny) {
          console.log(error);
          showNotification('error', t('Error', { ns: 'common' }), t('Error when resetting password', { ns: 'common' }));
        }
      }
    },
    [userId, changePassword, t, onClose],
  );

  return (
    <Modal isOpen={isOpened} onClose={onClose} width={380}>
      <Form form={form} layout="vertical" onFinish={resetPassword}>
        <div className="flex flex-col items-center justify-center gap-4 mb-4">
          <AppLogo width={180} height="100%" />
        </div>
        <Form.Item
          name="password"
          required={false}
          label={<span className="text-grey">{t('Enter a new password')}</span>}
          rules={[
            { required: true, message: t('Please enter your new password!', { ns: 'common' }) },
            { min: 8, message: t('Password must be at least 8 characters!', { ns: 'common' }) },
          ]}
        >
          <ModernInputPassword placeholder={t('New password')} />
        </Form.Item>
        <Form.Item
          name="repeatPassword"
          required={false}
          label={<span className="text-grey">{t('Confirm your new password')}</span>}
          rules={[
            { required: true, message: t('Please repeat password!', { ns: 'common' }) },
            ({ getFieldValue }) => ({
              async validator(_, value) {
                if (!value || getFieldValue('password') === value) {
                  await Promise.resolve();
                  return;
                }
                return await Promise.reject(new Error(t('Password must be the same', { ns: 'common' })));
              },
            }),
          ]}
        >
          <ModernInputPassword placeholder={t('Repeat password')} />
        </Form.Item>
        <Button containerClassName="mt-8" type="submit" isLoading={isLoading}>
          {t('Set new password')}
        </Button>
      </Form>
    </Modal>
  );
});
